// CPU renderer. Produces the same image as the WebGL preview (renderer.js) and
// is used for full-resolution exports inside the worker, where it avoids GPU
// memory limits. Also the reference implementation for tests.

import { blendPixel } from "./blendModes.js";

export const DEFAULT_APPEARANCE = Object.freeze({
  shadows: 1, // 0..2 strength of baked shadows
  highlights: 1, // 0..2 strength of baked highlights/reflections
  blur: 2, // design-space pixels
  opacity: 1, // 1 - transparency
  brightness: 0, // -100..100
  contrast: 0, // -100..100
});

const LIMITS = { shadows: [0, 2], highlights: [0, 2], blur: [0, 20], opacity: [0, 1], brightness: [-100, 100], contrast: [-100, 100] };

export function normalizeAppearance(value = {}) {
  const out = {};
  for (const [key, fallback] of Object.entries(DEFAULT_APPEARANCE)) {
    const v = value?.[key];
    out[key] = Number.isFinite(v) ? Math.max(LIMITS[key][0], Math.min(LIMITS[key][1], v)) : fallback;
  }
  return out;
}

/** Converts straight RGBA bytes to premultiplied bytes (in place copy). */
export function premultiply(data) {
  const out = new Uint8ClampedArray(data.length);
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    out[i] = (data[i] * a) / 255;
    out[i + 1] = (data[i + 1] * a) / 255;
    out[i + 2] = (data[i + 2] * a) / 255;
    out[i + 3] = a;
  }
  return out;
}

// Box sizes approximating a Gaussian with three passes (Kovesi).
function boxesForGauss(sigma, n = 3) {
  const wIdeal = Math.sqrt((12 * sigma * sigma) / n + 1);
  let wl = Math.floor(wIdeal);
  if (wl % 2 === 0) wl--;
  const wu = wl + 2;
  const mIdeal = (12 * sigma * sigma - n * wl * wl - 4 * n * wl - 3 * n) / (-4 * wl - 4);
  const m = Math.round(mIdeal);
  return Array.from({ length: n }, (_, i) => (i < m ? wl : wu));
}

function boxPass(src, dst, w, h, r, horizontal) {
  const len = horizontal ? w : h, lines = horizontal ? h : w;
  const step = horizontal ? 4 : w * 4;
  const norm = 1 / (2 * r + 1);
  for (let line = 0; line < lines; line++) {
    const start = horizontal ? line * w * 4 : line * 4;
    for (let c = 0; c < 4; c++) {
      let acc = 0;
      const at = (i) => src[start + Math.max(0, Math.min(len - 1, i)) * step + c];
      for (let i = -r; i <= r; i++) acc += at(i);
      for (let i = 0; i < len; i++) {
        dst[start + i * step + c] = acc * norm;
        acc += at(i + r + 1) - at(i - r);
      }
    }
  }
}

/** Gaussian blur (σ in pixels) of premultiplied RGBA bytes. */
export function blurPremultiplied(img, sigma) {
  if (!(sigma > 0.25)) return img;
  const { width: w, height: h } = img;
  let a = Float32Array.from(img.data), b = new Float32Array(a.length);
  for (const size of boxesForGauss(sigma)) {
    const r = (size - 1) / 2;
    boxPass(a, b, w, h, r, true);
    boxPass(b, a, w, h, r, false);
  }
  return { width: w, height: h, data: Uint8ClampedArray.from(a) };
}

/** Integer box downscale, used to prefilter artwork before bilinear sampling. */
export function reduce(img, f) {
  if (f < 2) return img;
  const w = Math.max(1, Math.floor(img.width / f)), h = Math.max(1, Math.floor(img.height / f));
  const out = new Uint8ClampedArray(w * h * 4), src = img.data, n = f * f;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      for (let c = 0; c < 4; c++) {
        let acc = 0;
        for (let j = 0; j < f; j++) {
          let p = ((y * f + j) * img.width + x * f) * 4 + c;
          for (let i = 0; i < f; i++, p += 4) acc += src[p];
        }
        out[(y * w + x) * 4 + c] = acc / n;
      }
    }
  }
  return { width: w, height: h, data: out };
}

/** Design-space pixels per output pixel, averaged over the surface. */
export function designDensity(template) {
  let covered = 0;
  for (let i = 0; i < template.coverage.length; i++) if (template.coverage[i]) covered++;
  if (!covered) return 1;
  return Math.sqrt((template.designWidth * template.designHeight) / covered);
}

/**
 * Prepares design-space artwork (designWidth × designHeight, straight RGBA,
 * placement already applied) for sampling: premultiply, blur, prefilter.
 */
export function prepareArtwork(art, template, appearance) {
  const pre = { width: art.width, height: art.height, data: premultiply(art.data) };
  // Blur is measured in Smart Object content pixels.
  const sigma = appearance.blur * (art.width / template.designWidth);
  const blurred = blurPremultiplied(pre, sigma);
  const density = designDensity(template) * (art.width / template.designWidth);
  return reduce(blurred, Math.floor(density));
}

const tmpBelow = [0, 0, 0], tmpShaded = [0, 0, 0], tmpBlend = [0, 0, 0];

/**
 * Renders a template with prepared artwork (from prepareArtwork). Artwork
 * covers the whole design space: texture coordinate = uv.
 * Returns opaque RGBA bytes of template.width × template.height.
 */
export function renderTemplate(template, art, appearanceInput) {
  const appearance = normalizeAppearance(appearanceInput);
  const { width, height, uv, coverage, below, lightGain, lightBias, lightBiasSigned, postGain, postBias, postBiasSigned, designBlend } = template;
  const out = new Uint8ClampedArray(width * height * 4);
  const S = appearance.shadows, H = appearance.highlights, opacity = appearance.opacity;
  const bright = appearance.brightness / 100, contrast = (100 + appearance.contrast) / 100;
  const aw = art.width, ah = art.height, ad = art.data;
  const sample = [0, 0, 0, 0];

  for (let p = 0; p < width * height; p++) {
    const q = p * 4;
    let r = below[q] / 255, g = below[q + 1] / 255, b = below[q + 2] / 255;
    const cov = coverage[p];
    if (cov) {
      const u = (uv[q] * 256 + uv[q + 1]) / 65535, v = (uv[q + 2] * 256 + uv[q + 3]) / 65535;
      bilinear(ad, aw, ah, u * aw - 0.5, v * ah - 0.5, sample);
      const da = sample[3];
      const a = da * (cov / 255) * opacity;
      if (a > 0) {
        tmpBelow[0] = r; tmpBelow[1] = g; tmpBelow[2] = b;
        for (let c = 0; c < 3; c++) {
          let d = sample[c] / da;
          d = Math.min(1, Math.max(0, (d + bright - 0.5) * contrast + 0.5));
          const gain = lightGain[q + c] / 255;
          const bias = lightBiasSigned ? (lightBias[q + c] / 255) * 2 - 1 : lightBias[q + c] / 255;
          const shadow = Math.max(0, 1 - gain - bias);
          const adjusted = Math.max(0, 1 - shadow * S - bias * H);
          tmpShaded[c] = Math.min(1, Math.max(0, d * adjusted + bias * H));
        }
        const B = designBlend === 0 ? tmpShaded : blendPixel(designBlend, tmpBelow, tmpShaded, tmpBlend);
        r = r * (1 - a) + B[0] * a;
        g = g * (1 - a) + B[1] * a;
        b = b * (1 - a) + B[2] * a;
      }
    }
    if (postGain) {
      const pb = (c) => (postBiasSigned ? (postBias[q + c] / 255) * 2 - 1 : postBias[q + c] / 255);
      r = r * (postGain[q] / 255) + pb(0);
      g = g * (postGain[q + 1] / 255) + pb(1);
      b = b * (postGain[q + 2] / 255) + pb(2);
    }
    // Uint8ClampedArray rounds to nearest on assignment.
    out[q] = r * 255;
    out[q + 1] = g * 255;
    out[q + 2] = b * 255;
    out[q + 3] = 255;
  }
  return out;
}

/** Bilinear sample of premultiplied bytes; returns floats in [0, 1]. */
function bilinear(data, w, h, x, y, out) {
  const x0 = Math.floor(x), y0 = Math.floor(y), fx = x - x0, fy = y - y0;
  const cx0 = Math.max(0, Math.min(w - 1, x0)), cx1 = Math.max(0, Math.min(w - 1, x0 + 1));
  const cy0 = Math.max(0, Math.min(h - 1, y0)), cy1 = Math.max(0, Math.min(h - 1, y0 + 1));
  const i00 = (cy0 * w + cx0) * 4, i10 = (cy0 * w + cx1) * 4, i01 = (cy1 * w + cx0) * 4, i11 = (cy1 * w + cx1) * 4;
  for (let c = 0; c < 4; c++) {
    const top = data[i00 + c] + (data[i10 + c] - data[i00 + c]) * fx;
    const bottom = data[i01 + c] + (data[i11 + c] - data[i01 + c]) * fx;
    out[c] = (top + (bottom - top) * fy) / 255;
  }
  return out;
}
